// Services
import SubscriptionService from "@/app/services/main/subscription/subscription-service"

// Mixins
import { toastMessageMixins } from "@/app/mixins/utils/toast-message-mixins"
import { tableMixins } from "@/app/mixins/utils/table-mixins"
import { dateMixins } from "@/app/mixins/utils/date-mixins"

// Components
const ModalDisapproveSubscriptionCancellationRequest = () =>
	import(
		/* webpackPrefetch: true, webpackChunkName: "modal-disapprove-subscription-cancellation-request"*/ "@/app/views/main-view/subscriptions/components/modals/modal-disapprove-subscription-cancellation-request/modal-disapprove-subscription-cancellation-request.vue"
	)

// Constants
import { STATUS_CODES, PAYMENT_FREQUENCY_DISPLAY, SUBSCRIPTION_STATUS } from "@/app/constants/constants"

export default {
	name: "SubscriptionDetailsPage",
	mixins: [toastMessageMixins, tableMixins, dateMixins],
	components: {
		ModalDisapproveSubscriptionCancellationRequest
	},
	data() {
		return {
			// Loading
			isAPILoading: false,
			isDisapproveAPILoading: false,

			// Subscription Details
			subscriptionId: null,
			subscriptionDetails: {},
			deliveryFee: 0.0,

			// Transaction Details
			transactionDetails: {},
			transactionHistory: null,

			// Products
			productDetails: null,
			activeProductId: null,

			// Order Details
			orderDetails: {},
			subtotal: 0,

			// Table
			theads: [
				{ name: "Image", sortLabel: null, isDesc: false, style: null },
				{ name: "Order ID", sortLabel: null, isDesc: false, style: null },
				{ name: "SKU", sortLabel: null, isDesc: false, style: null },
				{ name: "Products", sortLabel: null, isDesc: false, style: null },
				{ name: "Quantity", sortLabel: null, isDesc: false, style: null },
				{ name: "Price", sortLabel: null, isDesc: false, style: null },
				{ name: "Period", sortLabel: null, isDesc: false, style: null },
				{ name: "Status", sortLabel: null, isDesc: false, style: null },
				{ name: null, sortLabel: null, isDesc: false, style: null },
				// { name: 'Delivery Date', sortLabel: null, isDesc: false, style: null },
			],
			theadsDoggyPack: [
				{ name: "Image", sortLabel: null, isDesc: false, style: null },
				{ name: "SKU", sortLabel: null, isDesc: false, style: null },
				{ name: "Products", sortLabel: null, isDesc: false, style: null },
				{ name: "Quantity", sortLabel: null, isDesc: false, style: null },
				{ name: "Price", sortLabel: null, isDesc: false, style: null },
				{ name: null, sortLabel: null, isDesc: false, style: null },
				// { name: 'Delivery Date', sortLabel: null, isDesc: false, style: null },
			],
			modifyMode: "",
			selectedOrder: {},
		}
	},
	computed: {
		timelineItems() {
			let items = []

			// actions made by admin
			const adminManualActions = [
				SUBSCRIPTION_STATUS.CANCELLED, 
				SUBSCRIPTION_STATUS.APPROVED_CANCELLATION, 
				SUBSCRIPTION_STATUS.DISAPPROVED_CANCELLATION,
				SUBSCRIPTION_STATUS.DELETED,
			]

			if (this.subscriptionDetails && this.transactionHistory) {
				for (const data of this.transactionHistory) {
					const { transactionDate, status, remarks, submittedBy } = data;

					let transDate = this.formatDateTime(transactionDate);
					let content = SUBSCRIPTION_STATUS[status] ? SUBSCRIPTION_STATUS[status].replace("_", " ") : "";
					
					if (status === SUBSCRIPTION_STATUS.FOR_CANCELLATION) {
						content += `<br> <p class="color-warning mb-0">Reason: ${remarks}</p>`
					}
					else if ((adminManualActions.includes(status)) && submittedBy) {
                        content += `<br> <p class="color-warning mb-0">Remarks: ${remarks}</p>`;
                        content += `<p class="color-warning mb-0">Verified By: ${submittedBy}</p>`
                    }
					else{
                        content += `<br> <p class="color-warning mb-0">Remarks: ${remarks}</p>`;
                    }
					items.push({
						htmlMode: true,
						content: `[${transDate}] : ${content}`,
					})
				}
			}

			return items.reverse()
		},
		deliveryFeeText() {
			return this.deliveryFee != 0.0 ? `$${parseFloat(this.deliveryFee).toFixed(2)}` : "Free"
		},
		showCancellationInfo(){
			if(this.transactionHistory && this.transactionHistory.length > 0){
				const transactions = [...this.transactionHistory].reverse();

				const { status: transactionStatus } = transactions[0];
				
				// this status is when the admin manually cancels the subscription
				if(transactionStatus === SUBSCRIPTION_STATUS.CANCELLED){
					return false;
				}
				else{
					// display cancellation details if the status of the subscription is for cancellation or approved cancellation
					return this.subscriptionDetails.status === SUBSCRIPTION_STATUS.FOR_CANCELLATION || transactionStatus === SUBSCRIPTION_STATUS.APPROVED_CANCELLATION;
				}

			}
			return false;
		},
		forCancellationRemark() {
			if (this.transactionHistory) {
				const filteredTransactionHistory = this.transactionHistory.filter(
					(transaction) => transaction.status === SUBSCRIPTION_STATUS.FOR_CANCELLATION
				);

				if(!filteredTransactionHistory.length) return;

				const lastValue = filteredTransactionHistory.slice(-1)[0];

				return lastValue.remarks;
			} else {
				return "No Remarks";
			}
		},
	},
	created() {
		this.getSubscriptionDetails()
	},
	methods: {
		async getSubscriptionDetails() {
			this.isAPILoading = true
			this.subscriptionId = this.getSubscriptionId()

			const { ERROR_GET_SUBSCRIPTION_DETAILS } = this.toastMessages.subscription();

			try {
				const response = await SubscriptionService.getSubscriptionDetailsById(this.subscriptionId)

				if (response.status === STATUS_CODES.SUCCESS) {
					this.setDataFields(response.data)
				} else{
					this.showAPIErrorToast(ERROR_GET_SUBSCRIPTION_DETAILS, response);
				}
			} catch (error) {
				this.showGenericErrorToast();
			} finally{
				this.isAPILoading = false
			}
		},

		setDataFields(data) {
			this.subscriptionDetails = data
			this.transactionDetails = data.transactionDetails
			this.transactionHistory = data.transactionHistory
			this.orderDetails = data.orderDetails
			this.productDetails = data.bundles

			const { deliveryFee, totalPrice } = data;
			this.subtotal = deliveryFee ? parseFloat(totalPrice) - parseFloat(deliveryFee) : totalPrice;
		},

		// Subscription
		getSubscriptionId() {
			const routeArray = this.$route.path.split("/")
			const subscriptionId = routeArray[routeArray.length - 1]
			return subscriptionId
		},

		totalSubscriptionPrice(subtotal) {
			let total = parseFloat(this.deliveryFee) + parseFloat(subtotal)
			return total.toFixed(2)
		},
		showDisapproveModal() {
			// this.isDisapproveAPILoading = true
			this.$refs.modalDisapprove.openModal(this.subscriptionDetails)
		},
		handleSuccessDisapproved() {
			this.isDisapproveAPILoading = false
			this.getSubscriptionDetails();
		},
		onCloseModal() {
			this.isDisapproveAPILoading = false
		},
		subscriptionStatus(status) {
			const statusMap = {
				["A"]: "status--green",
				["I"]: "status--grey",
				[SUBSCRIPTION_STATUS.CANCELLED]: "status--red",
				[SUBSCRIPTION_STATUS.ENDED]: "status--red",
				[SUBSCRIPTION_STATUS.DELETED]: "status--red",
				[SUBSCRIPTION_STATUS.FOR_CANCELLATION]: "status--grey",
			}

			return statusMap[status] || "status--grey"
		},

		// Products
		setProductImage(img) {
			return img ? img : require("@/assets/images/_shared/product-default.png")
		},

		renderCollapsibleRow(productId) {
			return productId ? this.activeProductId === productId : false
		},

		showCollapsibleRow(productId) {
			if (this.activeProductId === productId) {
				this.activeProductId = !this.activeProductId ? productId : null
			} else {
				this.activeProductId = productId
			}
		},

		// Cancel Subscription
		cancelSubscription(action) {
			let cancellationTitle = action === "approval" ? "Approve Cancellation Request" : "Cancel Subscription";

			const { SUCCESS_CANCEL_SUBSCRIPTION, ERROR_CANCEL_SUBSCRIPTION } = this.toastMessages.subscription()
			this.$swal({
				title: cancellationTitle,
				text: `Are you sure you want to cancel this subscription?`,
				confirmButtonText: "Confirm",
				showCancelButton: true,
				cancelButtonText: "Cancel",
				reverseButtons: true,
				showCloseButton: true
			}).then(async (result) => {
				if (result.value) {
					this.isAPILoading = true

					let cancellationStatus = action === "approval" ? SUBSCRIPTION_STATUS.APPROVED_CANCELLATION : SUBSCRIPTION_STATUS.CANCELLED;

					const updateDetails = {
						status: cancellationStatus,
						remarks: `Your subscription ${this.subscriptionId} has been cancelled.`,
					}

					const response = await SubscriptionService.cancelSubscription(this.subscriptionId, updateDetails);

					try {
						if (response.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
							this.getSubscriptionDetails()
							this.showCustomSuccessToast(SUCCESS_CANCEL_SUBSCRIPTION)
						} else {
							this.showAPIErrorToast(ERROR_CANCEL_SUBSCRIPTION, response)
						}
						this.isAPILoading = false
					} catch (error) {
						this.showGenericErrorToast()
						this.isAPILoading = false
					}
				}
			})
		},

		// Delete Subscription
		deleteSubscription() {
			const { SUCCESS_DELETE_SUBSCRIPTION, ERROR_DELETE_SUBSCRIPTION } = this.toastMessages.subscription()

			this.$swal({
				title: "Delete Subscription",
				text: `Are you sure you want to delete this subscription?`,
				confirmButtonText: "Confirm",
				showCancelButton: true,
				cancelButtonText: "Cancel",
				reverseButtons: true,
				showCloseButton: true
			}).then(async (result) => {
				if (result.value) {
					this.isAPILoading = true
					const response = await SubscriptionService.deleteSubscription(this.subscriptionId)

					try {
						if (response.status === STATUS_CODES.SUCCESS_NO_CONTENT) {
							// this.getSubscriptionDetails()
							this.showCustomSuccessToast(SUCCESS_DELETE_SUBSCRIPTION)
							this.$router.push('/subscriptions')
						} else {
							this.showAPIErrorToast(ERROR_CANCEL_SUBSCRIPTION, response)
						}
						this.isAPILoading = false
					} catch (error) {
						this.showGenericErrorToast()
						this.isAPILoading = false
					}
				}
			})
		},
		displayFrequencyFormat(value) {
			return PAYMENT_FREQUENCY_DISPLAY[value]
		},
	},
}
